/**
 * Página de Detalhes da Área
 * Exibe informações completas da área com navegação por abas
 * 
 * @author Equipe GestaoeCRM
 * @date 31/10/2025
 * @version 1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Users,
  Building2,
  User,
  Mail,
  Shield,
  Activity,
  Plus,
  X,
  Check,
  AlertCircle,
  UserPlus,
  ArrowRightLeft,
  Filter
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import companyAreaService, { CompanyArea } from '@/services/companyAreaService';
import api from '@/services/api';
import LoadingSpinner from '@/components/LoadingSpinner';
import SelectAnalystModal from '@/components/SelectAnalystModal';
import AreaTimeline from '@/components/AreaTimeline';

interface TabConfig {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  component: React.ComponentType<{ areaId: string }>;
}

export default function AreaDetailsPage() {
  const { id: areaId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddAnalystModal, setShowAddAnalystModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Buscar detalhes da área
  const { data: area, isLoading, error } = useQuery({
    queryKey: ['company-area', areaId],
    queryFn: () => companyAreaService.getAreaById(areaId!),
    enabled: !!areaId,
  });

  // O SelectAnalystModal já faz o filtro, não precisa buscar separadamente

  // Mutação para vincular analista
  const linkAnalystMutation = useMutation({
    mutationFn: async (analystId: string) => {
      // Usar api service do projeto
      const response = await api.put(`/users/${analystId}`, {
        areaId: areaId,
      });
      return response.data;
    },
    onSuccess: () => {
      // Invalidar todas as queries relacionadas para forçar atualização
      queryClient.invalidateQueries({ queryKey: ['company-area', areaId] });
      queryClient.invalidateQueries({ queryKey: ['area-activities', areaId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['company-areas'] });
      
      // Refetch imediato para atualizar a timeline
      queryClient.refetchQueries({ queryKey: ['area-activities', areaId] });
      queryClient.refetchQueries({ queryKey: ['company-area', areaId] });
      
      toast.success('Analista vinculado com sucesso!');
      setShowAddAnalystModal(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erro ao vincular analista');
    },
  });

  const handleAddAnalyst = (analystId: string) => {
    linkAnalystMutation.mutate(analystId);
  };

  const getStatusBadge = (active: boolean) => {
    return active ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-400">
        <Check className="w-3 h-3 mr-1" />
        Ativa
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400">
        <X className="w-3 h-3 mr-1" />
        Inativa
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !area) {
    return (
      <div className="min-h-screen bg-background-glass-hover flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-text-primary mb-2">Área não encontrada</h2>
          <p className="text-sm text-text-muted mb-4">A área solicitada não foi encontrada.</p>
          <button
            onClick={() => navigate('/administration/company-areas')}
            className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
          >
            Voltar para Áreas
          </button>
        </div>
      </div>
    );
  }

  // Componente de Visão Geral
  const OverviewTab: React.FC<{ areaId: string }> = () => {
    return (
      <div className="space-y-4">
        {/* Informações Básicas */}
        <div className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-glass p-6">
          <h3 className="text-lg font-bold text-text-primary mb-4">Informações Básicas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">
                <Building2 className="w-4 h-4 inline mr-1" />
                Nome da Área
              </label>
              <p className="text-sm text-text-primary">{area.name}</p>
            </div>
            <div>
              <label className="block text-sm font-bold text-text-secondary mb-1">
                <Filter className="w-4 h-4 inline mr-1" />
                Status
              </label>
              <div className="mt-1">
                {getStatusBadge(area.active)}
              </div>
            </div>
          </div>
          
          {area.description && (
            <div className="mt-4">
              <label className="block text-sm font-bold text-text-secondary mb-1">
                Descrição
              </label>
              <p className="text-sm text-text-primary">{area.description}</p>
            </div>
          )}
          
          {area.manager && (
            <div className="mt-4">
              <label className="block text-sm font-bold text-text-secondary mb-1">
                <User className="w-4 h-4 inline mr-1" />
                Gestor da Área
              </label>
              <div className="flex items-center gap-2 mt-1">
                <div className="bg-background-glass-hover rounded-lg px-3 py-2 flex-1">
                  <p className="text-sm font-medium text-text-primary">{area.manager.name}</p>
                  <p className="text-xs text-text-muted">{area.manager.email}</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary/20 text-secondary mt-1">
                    {area.manager.role}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Usuários Vinculados */}
        <div className="bg-background-glass backdrop-blur-glass border border-border-glass rounded-glass p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-text-primary flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Usuários Vinculados ({area.users?.length || 0})
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
                <>
                  <button
                    onClick={() => setShowAddAnalystModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-border-glass rounded-button shadow-glass text-xs sm:text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
                    title="Adicionar Analista"
                  >
                    <UserPlus className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Adicionar Analista</span>
                  </button>
                  <button
                    onClick={() => setShowTransferModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-button shadow-glass text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110"
                    title="Transferir Usuários"
                  >
                    <ArrowRightLeft className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Transferir Usuários</span>
                  </button>
                </>
              )}
            </div>
          </div>
          
          {!area.users || area.users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-text-muted opacity-50" />
              <p className="mt-2 text-sm text-text-muted">
                Nenhum usuário vinculado a esta área
              </p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {area.users.map((user) => (
                <div
                  key={user.id}
                  className="bg-background-glass-hover rounded-lg px-4 py-3 hover:bg-background-glass transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-text-primary">{user.name}</p>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {user.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-text-muted flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </span>
                        <span className="text-xs text-secondary flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          {user.role}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Configuração das abas
  const tabs: TabConfig[] = [
    {
      id: 'overview',
      label: 'Visão Geral',
      icon: Building2,
      component: OverviewTab
    },
    {
      id: 'timeline',
      label: 'Timeline',
      icon: Activity,
      component: AreaTimeline
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className="min-h-screen bg-background-glass-hover">
      {/* Header */}
      <div className="bg-background-glass backdrop-blur-glass shadow-glass border-b border-border-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between py-4 gap-4">
            <div className="flex-1 min-w-0 w-full lg:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-2">
                <button
                  onClick={() => navigate('/administration/company-areas')}
                  className="flex items-center text-text-muted hover:text-text-secondary transition-all duration-300 w-fit"
                >
                  <ArrowLeft className="h-5 w-5 mr-1 flex-shrink-0" />
                  <span>Voltar</span>
                </button>
                
                <div className="flex items-center flex-wrap gap-2">
                  {getStatusBadge(area.active)}
                </div>
              </div>
              
              <h1 className="text-lg sm:text-xl font-semibold text-text-primary break-words">
                {area.name}
              </h1>
              
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-6 mt-2 text-xs sm:text-sm text-text-muted">
                <div className="flex items-center min-w-0">
                  <Users className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="truncate">{area.users?.length || 0} usuário(s)</span>
                </div>
                {area.manager && (
                  <div className="flex items-center min-w-0">
                    <User className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="truncate">Gestor: {area.manager.name}</span>
                  </div>
                )}
                <div className="flex items-center">
                  <Activity className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>Criada em {new Date(area.createdAt).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-row lg:flex-row items-center gap-2 sm:gap-3 w-full lg:w-auto lg:ml-6">
              {(user?.role === 'ADMIN' || user?.role === 'GESTOR') && (
                <button 
                  onClick={() => navigate(`/administration/company-areas?edit=${areaId}`)}
                  className="flex-1 lg:flex-none inline-flex items-center justify-center px-3 py-2 border border-border-glass shadow-glass text-xs sm:text-sm leading-4 font-bold rounded-button text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover focus:outline-none focus:ring-2 focus:ring-offset-background-dark focus:ring-blue-500"
                >
                  <Edit className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Editar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Conteúdo principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navegação por abas */}
        <div className="bg-background-glass backdrop-blur-glass rounded-glass shadow-glass border mb-6">
          <div className="border-b border-border-glass">
            <nav className="-mb-px flex overflow-x-auto space-x-4 sm:space-x-8 px-4 sm:px-6 scrollbar-hide">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-bold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${
                      isActive
                        ? 'border-blue-500 text-secondary'
                        : 'border-transparent text-text-muted hover:text-text-secondary hover:border-border-glass'
                    }`}
                  >
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.substring(0, 3)}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
        
        {/* Conteúdo da aba ativa */}
        <div className="space-y-4">
          {ActiveComponent && <ActiveComponent areaId={areaId!} />}
        </div>
      </div>

      {/* Modal para adicionar analista */}
      {showAddAnalystModal && (
        <SelectAnalystModal
          isOpen={showAddAnalystModal}
          onClose={() => setShowAddAnalystModal(false)}
          onSelect={handleAddAnalyst}
          title="Adicionar Analista à Área"
          description="Selecione um analista que não possui área definida para vincular a esta área"
          filterWithoutArea={true}
        />
      )}

      {/* Modal para transferência */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-background-glass backdrop-blur-glass rounded-glass shadow-glass border border-border-glass w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-text-primary">Transferir Usuários</h3>
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="p-2 text-text-muted hover:text-text-primary"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-text-muted mb-4">
                Esta funcionalidade permite transferir usuários entre áreas. 
                Clique no botão abaixo para abrir a página de transferência.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    navigate(`/administration/company-areas/transfer?areaId=${areaId}`);
                  }}
                  className="px-4 py-2 border border-transparent rounded-button shadow-glass text-sm font-bold text-white bg-gradient-to-r from-primary to-secondary hover:brightness-110"
                >
                  Abrir Transferência
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

